import { logger } from 'shared/logging';
import { ApiHelper } from 'shared/api';
import { ROUTES, getUrl } from 'shared/api/urls';
import { queryStringFromParams } from 'shared/helpers/utils/url';
import {
  OFFER_API_ERR_MESSAGE,
  platformNames,
  DYNAMIC_FOOTER_LINK_API_ERR_MESSAGE,
} from 'shared/constants';
import { AdditionalRequestOptions } from 'api/types';

import {
  transformProductData,
  transformProductOffers,
  transformProductCoupons,
  transformDynamicFooterLinks,
  transformPassDetails,
} from './transformer';
import { OffersParams, FetchProductParams } from './types';
import { State } from 'store/types';
import { transformProductCoupons as couponDataTranformer } from '../coupons/transformer';
import { HttpMethods } from 'constants/requestHeaders';
import { isMen } from '@nykaa/seo/utils';
import transformFreeGiftOffers from 'layouts/Product/FreeGifts/transformer';

const mandatoryRequestParams = {
  app_version: '2.0.6',
  source: 'react',
  variants: true,
};

export const getProduct = async (
  {
    id,
    skuId,
    store,
    lang,
    pro,
    enablePR,
    priveBand,
    regionId,
    internalSlug,
    paid_early_access,
    pincode,
    lat,
    long,
  }: FetchProductParams,
  state: State
) => {
  const apiUrl = ROUTES.PRODUCT;
  const url = getUrl(apiUrl);
  const queryParams = {
    ...mandatoryRequestParams,
    product_id: id,
    ...(store && { store }), // Optionally add store if it exists
    ...(lang && { lang }), // Optionally add lang if it exists
    ...(pro && { pro }),
    ...(enablePR && { enable_pr: '1' }),
    ...(priveBand && { customer_tier: isMen() ? 'non_prive' : priveBand }),
    ...(regionId && {
      region_id: regionId,
      internal_slug: internalSlug,
    }),
    ...(paid_early_access && { paid_early_access: true }),
    ...(pincode && { pincode }),
    ...(lat && long && { lat, long }),
  };
  const queryString = queryStringFromParams(queryParams);
  try {
    const {
      data: { response },
    } = await ApiHelper(`${url}${queryString}`, 'get');
    if (!response) {
      throw new Error('"response" not found in api -> data');
    }
    return transformProductData(response, skuId, state);
  } catch (e) {
    // * https://github.com/axios/axios/issues/472#issuecomment-252584718
    const ERROR_FETCHING_PRODUCT = 'Error fetching product';
    logger.error(e, `${ERROR_FETCHING_PRODUCT} :url=${url}`, {
      apiUrl: `${url}${queryString}`,
    });
    throw e;
  }
};

export const getProductOffers = async (params: OffersParams) => {
  const queryParams = {
    ...params,
    ...mandatoryRequestParams,
  };
  let domainParams = {};

  if (__PLATFORM__ === platformNames.MEN) {
    domainParams = { domain: __PLATFORM__ };
  }
  const productId = params.productId;
  /// FIXME: move this  to some util
  const url = `/gateway-api/offer/api/v1/product/${productId}/offer`;
  const queryString = queryStringFromParams({
    ...queryParams,
    ...domainParams,
  });
  const optionals: AdditionalRequestOptions = { CATALOG_TAG_FILTER: false };

  try {
    const { data: response } = await ApiHelper(
      `${url}${queryString}`,
      'get',
      {},
      {},
      optionals
    );
    const { data: offers = [] } = response || {};

    return transformProductOffers(offers);
  } catch (e) {
    throw e;
  }
};

export const getProductOffersAndCoupons = async (
  params: OffersParams,
  isOfferApiV2Enabled?: boolean,
  defaultDescForCoupons?: string,
  bestPriceEnabled?: boolean
) => {
  const productId = params.productId;
  const queryParams = {
    ...params,
    ...mandatoryRequestParams,
    productId,
  };
  let domainParams = {};

  if (__PLATFORM__ === platformNames.MEN) {
    domainParams = { domain: __PLATFORM__ };
  }
  const url = getUrl(
    isOfferApiV2Enabled || bestPriceEnabled
      ? ROUTES.COUPONS_OFFERS_V2
      : ROUTES.COUPONS_OFFERS
  );
  const queryString = queryStringFromParams({
    ...queryParams,
    ...domainParams,
  });
  const optionals: AdditionalRequestOptions = { CATALOG_TAG_FILTER: false };

  try {
    const { data: response } = await ApiHelper(
      `${url}${queryString}`,
      'get',
      {},
      {},
      optionals
    );
    const { data } = response || {};
    const offersAndCoupons = {
      offers: transformProductOffers(data?.offers),
      coupons: transformProductCoupons(data?.coupons, defaultDescForCoupons),
      bestOfferDetail: {
        ...data?.bestOfferDetail,
        coupons: couponDataTranformer(data?.bestOfferDetail?.coupons),
      },
      personalizedBestOfferDetail: {
        ...data?.personalizedBestOfferDetail,
        coupons: couponDataTranformer(
          data?.personalizedBestOfferDetail?.coupons
        ),
      },
      freeGiftOffers: transformFreeGiftOffers(data?.freeGiftOffers),
      passDetails: transformPassDetails(data?.passDetailsDTO),
    };
    return offersAndCoupons;
  } catch (e) {
    logger.error(e, OFFER_API_ERR_MESSAGE);
    throw e;
  }
};

export const getDynamicFooterLinks = async (params: any = {}) => {
  const url = getUrl(ROUTES.DYNAMIC_FOOTER_LINKS);
  const { l1Id, l2Id, l3Id } = params;
  let queryParams = {
    domain: __PLATFORM__,
    l1_id: l1Id,
    l2_id: l2Id,
    l3_id: l3Id,
  };
  const queryString = queryStringFromParams(queryParams);
  try {
    const {
      data: { response },
    } = await ApiHelper(`${url}${queryString}`, HttpMethods.GET);

    return transformDynamicFooterLinks(response);
  } catch (e) {
    logger.error(e, `${DYNAMIC_FOOTER_LINK_API_ERR_MESSAGE} :url=${url}`);
    throw e;
  }
};
